import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Play } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { clearAuthSession, getAuthToken } from '../utils/auth';
import { canStartInterview } from '../utils/interviewEligibility';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';

async function fetchVacancyDetails(vacancyId, token) {
  const response = await fetch(`${API_BASE_URL}/api/vacancies/${vacancyId}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));
  return { response, data };
}

export default function VacancyDetailsScreen() {
  const navigate = useNavigate();
  const { vacancyId } = useParams();
  const authModal = useAuthModal();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vacancy, setVacancy] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitErrorMessage, setLimitErrorMessage] = useState('');
  const [startingInterview, setStartingInterview] = useState(false);

  const loadVacancy = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      authModal.openAuth('search');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { response, data } = await fetchVacancyDetails(vacancyId, token);

      if (response.status === 401) {
        clearAuthSession();
        navigate('/', {
          replace: true,
          state: { openAuth: true, action: 'search', redirectTo: `/vacancy/${vacancyId}` },
        });
        return;
      }

      if (!response.ok) {
        setError(data.detail || data.message || 'Failed to load vacancy');
        return;
      }

      setVacancy(data);
    } catch (err) {
      console.error('[Vacancy] Load error:', err);
      setError('An error occurred while loading the vacancy.');
    } finally {
      setLoading(false);
    }
  }, [authModal, navigate, vacancyId]);

  useEffect(() => {
    if (vacancyId) {
      loadVacancy();
      return;
    }
    setLoading(false);
    setError('Vacancy not found.');
  }, [vacancyId, loadVacancy]);

  const title = vacancy?.title || 'Uploaded vacancy';

  const handleStartInterview = async () => {
    if (startingInterview) return;
    setStartingInterview(true);
    try {
      const check = await canStartInterview({ vacancyId });
      if (check.unauthorized) {
        authModal.openAuth('search', `/vacancy/${vacancyId}`);
        return;
      }
      if (!check.ok) {
        setLimitErrorMessage(check.message || '');
        setShowLimitModal(true);
        return;
      }

      navigate(`/chat-setting/${vacancyId}`, {
        state: { vacancyTitle: title, vacancyId, source: 'manual_vacancy' },
      });
    } finally {
      setStartingInterview(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B]">
      <SiteHeader authModal={authModal} />

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-6 pb-28">
        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => navigate('/search')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to search
          </button>

          <button
            type="button"
            onClick={handleStartInterview}
            className="btn-orange px-5 py-2 text-sm rounded-2xl shadow-lg shadow-orange-500/20 flex items-center gap-2"
            disabled={!vacancyId || startingInterview}
          >
            <Play className="w-4 h-4" />
            {startingInterview ? 'Checking access...' : 'Start AI Interview'}
          </button>
        </div>

        {loading && (
          <div className="bg-white rounded-[24px] p-6 text-center text-gray-500 shadow-sm">
            Loading vacancy...
          </div>
        )}

        {!loading && error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && vacancy && (
          <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm space-y-4">
            <div className="flex items-start justify-between gap-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold">{title}</h1>
                <p className="text-xs text-gray-400 mt-2 break-all">{vacancy.vacancy_id}</p>
              </div>
              <span className="px-3 py-1 rounded-full border border-orange-200 bg-orange-50 text-xs font-semibold text-orange-600">
                Manual
              </span>
            </div>

            <div className="bg-[#F6F7FB] rounded-2xl p-5">
              <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {vacancy.text}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <SubscriptionLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        errorMessage={limitErrorMessage}
      />
    </div>
  );
}
